import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import { ScreenPath, TabPath } from "@/constants/Navigation";
import { filterToAllowedUrlParams } from "@/constants/WebSessionRouteParams";

const ReturnToSessionStorageKey = "returnTo";

const HomeScreenPath = `/${TabPath.Home}`;

const ExcludedReturnToScreenPaths = new Set<string>([
  // /logout is an intermediate screen, not a destination to return to after login
  `/${ScreenPath.Logout}`,
  // the auth0 callback page is just a temporary redirect, not a URL to be "sent back to" after login
  `/${ScreenPath.WebAuth0Callback}`,
  // "/" is either the default post-login route anyhow,
  // or ScreenPath.Login (since expo strips app/(login)), which should not be redirected back to after login
  ScreenPath.Root,
]);

const saveReturnToUrl = ({
  pathname,
  search,
}: {
  pathname: string;
  search: string;
}): void => {
  const storage = globalThis.window.sessionStorage;
  if (isNil(storage)) return;

  if (ExcludedReturnToScreenPaths.has(pathname)) {
    return;
  }

  // bare /home is the destination postLoginRouting already replaces to, so "restoring" it would push a duplicate home onto the (home) nav stack
  // with expo-router/react-navigation, that first copy stays mounted, simply with `display: none`, which doubles DOM resource usage (nodes, memory, CPU)
  // BUT /home carrying an allowlisted sub-deep-link (eg /home?target=task&task-id=...) is a real destination worth restoring
  if (pathname === HomeScreenPath) {
    const allowedParams = filterToAllowedUrlParams(search);
    if (isEmpty(allowedParams)) return;
    const filteredSearch = `?${new URLSearchParams(allowedParams).toString()}`;
    storage.setItem(ReturnToSessionStorageKey, pathname + filteredSearch);
    return;
  }

  const url = pathname + search;
  storage.setItem(ReturnToSessionStorageKey, url);
};

const readAndClearReturnToUrl = (): null | string => {
  const storage = globalThis.window.sessionStorage;
  if (isNil(storage)) return null;

  const value = storage.getItem(ReturnToSessionStorageKey);
  // to prevent future stale reads of this value, delete it immediately
  storage.removeItem(ReturnToSessionStorageKey);
  return value;
};

export {
  ExcludedReturnToScreenPaths,
  readAndClearReturnToUrl,
  ReturnToSessionStorageKey,
  saveReturnToUrl,
};
